export const CHARACTER_RADIUS = .34;
export const CHARACTER_HEIGHT = 1.48;
const SKIN = .04, EPSILON = 1e-7, SEPARATION = 1e-5;
const finite = (value, fallback) => Number.isFinite(value) ? value : fallback;
const positive = (value, fallback) => Number.isFinite(value) && value >= 0 ? value : fallback;
const clamp = (value, low, high) => Math.max(low, Math.min(high, value));
const smooth = t => t * t * (3 - 2 * t);
const pointValid = p => p && Number.isFinite(p.x) && Number.isFinite(p.z) && Math.abs(p.x) <= 1e6 && Math.abs(p.z) <= 1e6;

function normalize(colliders, padding, ignoreIds = []) {
  const ignored = new Set(ignoreIds), result = [];
  for (let index = 0; index < (colliders?.length || 0); index++) {
    const source = colliders[index]; if (!source || ignored.has(source.id)) continue;
    const id = source.id ?? `collider-${index}`, bottom = finite(source.bottom, -Infinity), top = finite(source.top, Infinity);
    if (top <= bottom) continue;
    const common = { id, bottom, top, low: source.low };
    if (source.type === 'box') {
      if (![source.x0,source.x1,source.z0,source.z1].every(Number.isFinite)) continue;
      const x0=Math.min(source.x0,source.x1)-padding, x1=Math.max(source.x0,source.x1)+padding, z0=Math.min(source.z0,source.z1)-padding, z1=Math.max(source.z0,source.z1)+padding;
      result.push({ ...common, type:'box', x0,x1,z0,z1, x:(x0+x1)/2,z:(z0+z1)/2, halfWidth:(x1-x0)/2,halfDepth:(z1-z0)/2,angle:0 });
    } else if (source.type === 'oriented-box') {
      if (![source.x,source.z,source.halfWidth,source.halfDepth].every(Number.isFinite) || source.halfWidth<0 || source.halfDepth<0) continue;
      result.push({ ...common, type:'oriented-box', x:source.x,z:source.z,halfWidth:source.halfWidth+padding,halfDepth:source.halfDepth+padding,angle:finite(source.angle,0) });
    } else if (pointValid(source) && Number.isFinite(source.r) && source.r >= 0) {
      result.push({ ...common, type:'circle', x:source.x,z:source.z,r:source.r+padding });
    }
  }
  return result;
}

export function collisionObstacles(colliders, { radius = CHARACTER_RADIUS, skin = SKIN } = {}) {
  return normalize(colliders,positive(radius,CHARACTER_RADIUS)+positive(skin,SKIN)).map(c => {
    if(c.type==='box') return {id:c.id,type:c.type,x0:c.x0,x1:c.x1,z0:c.z0,z1:c.z1,bottom:c.bottom,top:c.top,low:c.low};
    return {...c};
  });
}
const verticalOverlap = (c, feetY, height) => feetY < c.top-EPSILON && feetY+height > c.bottom+EPSILON;
const local = (c,p) => {const cos=Math.cos(c.angle),sin=Math.sin(c.angle),dx=p.x-c.x,dz=p.z-c.z;return {x:cos*dx-sin*dz,z:sin*dx+cos*dz};};
const worldNormal = (c,n) => {const cos=Math.cos(c.angle),sin=Math.sin(c.angle);return {x:cos*n.x+sin*n.z,z:-sin*n.x+cos*n.z};};
function contains(c,p,inset=0) {
  if(c.type==='circle') return Math.hypot(p.x-c.x,p.z-c.z)<c.r-inset;
  const q=local(c,p); return Math.abs(q.x)<c.halfWidth-inset && Math.abs(q.z)<c.halfDepth-inset;
}

// Segment parameter interval occupied by an inflated footprint, independent of
// frame size. Boxes use local slab intersection; circles use the quadratic.
function interval(c,start,delta) {
  if(c.type==='circle') {
    const x=start.x-c.x,z=start.z-c.z,a=delta.x*delta.x+delta.z*delta.z;
    if(a<EPSILON*EPSILON) return contains(c,start)?{enter:0,exit:1,normal:{x:1,z:0}}:null;
    const b=x*delta.x+z*delta.z,discriminant=b*b-a*(x*x+z*z-c.r*c.r);
    if(discriminant<0) return null;
    const root=Math.sqrt(Math.max(0,discriminant)),enter=(-b-root)/a,exit=(-b+root)/a;
    if(exit<0 || enter>1) return null;
    const nx=x+delta.x*enter,nz=z+delta.z*enter,length=Math.hypot(nx,nz);
    return {enter,exit,normal:length>EPSILON?{x:nx/length,z:nz/length}:{x:1,z:0}};
  }
  const p=local(c,start),end=local(c,{x:start.x+delta.x,z:start.z+delta.z}),d={x:end.x-p.x,z:end.z-p.z};
  let enter=-Infinity,exit=Infinity,normal={x:0,z:0};
  for(const axis of ['x','z']) {
    const half=axis==='x'?c.halfWidth:c.halfDepth;
    if(Math.abs(d[axis])<EPSILON) {if(Math.abs(p[axis])>half) return null;continue;}
    let first=(-half-p[axis])/d[axis],last=(half-p[axis])/d[axis],sign=-1;
    if(first>last) {const temp=first;first=last;last=temp;sign=1;}
    if(first>enter+EPSILON) {enter=first;normal={x:0,z:0};normal[axis]=sign;}
    else if(Math.abs(first-enter)<=EPSILON) normal[axis]=sign;
    exit=Math.min(exit,last);
    if(enter>exit+EPSILON) return null;
  }
  if(exit<0 || enter>1) return null;
  const length=Math.hypot(normal.x,normal.z); if(length>EPSILON) {normal.x/=length;normal.z/=length;}
  return {enter,exit,normal:worldNormal(c,normal)};
}
function pushOut(c,p,desiredDelta) {
  if(c.type==='circle') {
    let x=p.x-c.x,z=p.z-c.z,length=Math.hypot(x,z);
    if(length<EPSILON) {x=desiredDelta.x;z=desiredDelta.z;length=Math.hypot(x,z);if(length<EPSILON){x=1;length=1;}}
    return {x:c.x+x/length*(c.r+SEPARATION),z:c.z+z/length*(c.r+SEPARATION)};
  }
  const q=local(c,p),dx=c.halfWidth-Math.abs(q.x),dz=c.halfDepth-Math.abs(q.z);
  const axis=dx<=dz?'x':'z',half=axis==='x'?c.halfWidth:c.halfDepth;
  q[axis]=(q[axis]<0?-1:1)*(half+SEPARATION);
  const n=worldNormal(c,q); return {x:c.x+n.x,z:c.z+n.z};
}

export function resolveCharacterStep(start, desired, colliders, { radius = CHARACTER_RADIUS, skin = SKIN, feetY = 0, height = CHARACTER_HEIGHT, ignoreIds = [] } = {}) {
  if(!pointValid(start)) return {x:0,z:0,blocked:true,hits:[]};
  if(!pointValid(desired)) return {x:start.x,z:start.z,blocked:true,hits:[]};
  const active=normalize(colliders,positive(radius,CHARACTER_RADIUS)+positive(skin,SKIN),ignoreIds).filter(c=>verticalOverlap(c,finite(feetY,0),positive(height,CHARACTER_HEIGHT)));
  const hits=new Set(),requested={x:desired.x-start.x,z:desired.z-start.z};
  let current={x:start.x,z:start.z};
  // Repair old saved positions minimally before applying this frame's motion.
  // It never authorizes a route straight through an initially containing prop.
  for(let pass=0;pass<12;pass++) {
    const c=active.find(c=>contains(c,current,EPSILON)); if(!c)break;
    current=pushOut(c,current,requested);hits.add(c.id);
  }
  let delta={...requested};
  for(let pass=0;pass<6 && Math.hypot(delta.x,delta.z)>EPSILON;pass++) {
    let nearest=null;
    for(const c of active) {
      const hit=interval(c,current,delta); if(!hit || hit.enter< -EPSILON || hit.enter>1)continue;
      if(delta.x*hit.normal.x+delta.z*hit.normal.z>=-EPSILON)continue;
      if(!nearest || hit.enter<nearest.enter)nearest={...hit,id:c.id};
    }
    if(!nearest) {current.x+=delta.x;current.z+=delta.z;delta={x:0,z:0};break;}
    const t=clamp(nearest.enter,0,1),n=nearest.normal;
    current.x+=delta.x*t+n.x*SEPARATION;current.z+=delta.z*t+n.z*SEPARATION;hits.add(nearest.id);
    delta={x:delta.x*(1-t),z:delta.z*(1-t)};
    const inward=delta.x*n.x+delta.z*n.z;
    if(inward<0) {delta.x-=inward*n.x;delta.z-=inward*n.z;}
  }
  return {x:current.x,z:current.z,blocked:hits.size>0,hits:[...hits]};
}

export function createVault(start, directionOrDestination, colliders, {
  radius = CHARACTER_RADIUS, skin = SKIN, feetY, height = CHARACTER_HEIGHT,
  maxHeight = .65, maxDistance = 2.8, maxDrop = .75, groundHeightAt = () => 0, canStandAt = () => true, ignoreIds = [],
} = {}) {
  if(!pointValid(start) || !directionOrDestination || typeof groundHeightAt!=='function' || typeof canStandAt!=='function')return null;
  const maxLength=clamp(positive(maxDistance,2.8),.1,12),bodyHeight=positive(height,CHARACTER_HEIGHT),heightLimit=positive(maxHeight,.65),dropLimit=Math.min(.75,positive(maxDrop,.75));
  const directionMode=Number.isFinite(directionOrDestination.dx)&&Number.isFinite(directionOrDestination.dz);
  let dx=directionMode?directionOrDestination.dx:directionOrDestination.x-start.x,dz=directionMode?directionOrDestination.dz:directionOrDestination.z-start.z;
  const requestedLength=Math.hypot(dx,dz);if(!Number.isFinite(requestedLength)||requestedLength<EPSILON)return null;
  const length=directionMode?maxLength:requestedLength;if(length>maxLength+EPSILON)return null;
  dx/=requestedLength;dz/=requestedLength;
  const startGround=groundHeightAt(start.x,start.z),startFeet=finite(feetY,finite(start.y,startGround));
  if(!Number.isFinite(startGround)||!Number.isFinite(startFeet)||!canStandAt(start.x,start.z))return null;
  const active=normalize(colliders,positive(radius,CHARACTER_RADIUS)+positive(skin,SKIN),ignoreIds);
  if(active.some(c=>contains(c,start,EPSILON)&&verticalOverlap(c,startFeet,bodyHeight)))return null;
  const delta={x:dx*length,z:dz*length};
  const crossings=active.map(c=>({c,hit:interval(c,start,delta)})).filter(row=>row.hit&&row.hit.exit>0&&row.hit.enter<1);
  // A downhill approach can put the current feet slightly above a prop that
  // will intersect the body as the ground drops. Evaluate the actual occupied
  // interval against local standing feet, not only against the takeoff height.
  const feetOffset=clamp(startFeet-startGround,0,.08);
  const localBand = (row, endFraction=1) => {
    const begin=Math.max(0,row.hit.enter),end=Math.min(endFraction,row.hit.exit);
    if(end<begin)return {valid:true,blocking:false,relativeHeight:0};
    const count=Math.max(1,Math.ceil((end-begin)*length/.025));
    let blocking=false,relativeHeight=-Infinity;
    for(let i=0;i<=count;i++) {
      const t=begin+(end-begin)*i/count,g=groundHeightAt(start.x+delta.x*t,start.z+delta.z*t);
      if(!Number.isFinite(g))return {valid:false,blocking:true,relativeHeight:Infinity};
      const overlaps=verticalOverlap(row.c,g+feetOffset,bodyHeight);
      blocking ||= overlaps;
      relativeHeight=Math.max(relativeHeight,row.c.top-g);
      // Held input against a known high wall must not ray-sample its entire
      // footprint every frame just to reach the same forbidden verdict.
      if(overlaps && (row.c.low===false || relativeHeight>heightLimit+EPSILON))return {valid:true,blocking:true,relativeHeight};
    }
    return {valid:true,blocking,relativeHeight};
  };
  const lowCrossings=crossings.map(row=>({...row,band:localBand(row)})).filter(row=>row.band.blocking).sort((a,b)=>a.hit.enter-b.hit.enter);
  if(!lowCrossings.length)return null;
  const vaultable=row=>row.band.valid && row.c.low!==false && Number.isFinite(row.c.top) && row.band.relativeHeight<=heightLimit+EPSILON;
  const first=lowCrossings[0];if(!vaultable(first)||Math.max(0,first.hit.enter)*length>1.3)return null;
  let exit=first.hit.exit,top=first.c.top;
  const vaulted=new Set([first.c.id]);
  // A close cluster can be crossed together; an intervening tall/forbidden prop
  // can never be silently added to a low-obstacle vault.
  for(const row of lowCrossings.slice(1)) {
    if(row.hit.enter*length>exit*length+.20)break;
    if(!vaultable(row))return null;
    exit=Math.max(exit,row.hit.exit);top=Math.max(top,row.c.top);vaulted.add(row.c.id);
  }
  const travel=directionMode?exit*length+.20:length;
  if(travel>maxLength+EPSILON || travel<exit*length+.08)return null;
  // Explicit destinations may span several separated low props. Validate all
  // crossed props too, rather than permitting a short forbidden pool wall.
  for(const row of lowCrossings) {
    if(row.hit.enter*length>travel+EPSILON || row.hit.exit<0)continue;
    const band=localBand(row,travel/length);
    if(!band.blocking)continue;
    if(!vaultable({...row,band}))return null;
    top=Math.max(top,row.c.top);vaulted.add(row.c.id);
  }
  const end={x:start.x+dx*travel,z:start.z+dz*travel},endGround=groundHeightAt(end.x,end.z);
  if(!Number.isFinite(endGround)||endGround-startGround>heightLimit+.1||startGround-endGround>dropLimit||!canStandAt(end.x,end.z))return null;
  if(active.some(c=>contains(c,end,-EPSILON)&&verticalOverlap(c,endGround,bodyHeight)))return null;
  let peakFeet=Math.max(startFeet,endGround,top+.08)+.05;
  const samples=Math.max(32,Math.ceil(travel/.025));
  for(let i=0;i<=samples;i++) {
    const t=i/samples,x=start.x+dx*travel*t,z=start.z+dz*travel*t,g=groundHeightAt(x,z);
    if(!Number.isFinite(g)||!canStandAt(x,z)||g-startGround>heightLimit+.1||startGround-g>dropLimit)return null;
    peakFeet=Math.max(peakFeet,g+.05);
  }
  // The crossing is straight in XZ: exact intervals catch even tiny overhead
  // props between terrain samples, including bands above standing height.
  const travelFraction=travel/length;
  if(crossings.some(row=>row.hit.enter<=travelFraction+EPSILON && row.hit.exit>=0 && verticalOverlap(row.c,peakFeet,bodyHeight)))return null;
  for(const p of [{x:start.x,z:start.z,from:startFeet},{...end,from:endGround}]) {
    if(active.some(c=>contains(c,p,-EPSILON)&&p.from<c.top-EPSILON&&peakFeet+bodyHeight>c.bottom+EPSILON))return null;
  }
  return {start:{x:start.x,y:startFeet,z:start.z},end:{...end,y:endGround},colliderIds:[...vaulted],height:peakFeet-startFeet,peakFeet,maxObstacleTop:top,
    distance:travel,duration:clamp(.68+travel*.12,.72,1.2),elapsed:0,groundHeightAt};
}

export function stepVault(plan, dt) {
  if(!plan || !pointValid(plan.start)||!pointValid(plan.end)||![plan.start.y,plan.end.y,plan.peakFeet,plan.maxObstacleTop,plan.duration].every(Number.isFinite)||plan.duration<=0)return null;
  plan.elapsed=clamp(finite(plan.elapsed,0)+Math.max(0,finite(dt,0)),0,plan.duration);
  const progress=plan.elapsed/plan.duration;let x=plan.start.x,z=plan.start.z,feetY,phase;
  if(progress<.22) {phase='rise';feetY=plan.start.y+(plan.peakFeet-plan.start.y)*smooth(progress/.22);}
  else if(progress<.76) {phase='cross';const t=(progress-.22)/.54,u=smooth(t);x+= (plan.end.x-plan.start.x)*u;z+=(plan.end.z-plan.start.z)*u;feetY=plan.peakFeet+.04*Math.sin(Math.PI*t);}
  else {phase=progress>=1?'done':'land';x=plan.end.x;z=plan.end.z;feetY=plan.peakFeet+(plan.end.y-plan.peakFeet)*smooth((progress-.76)/.24);}
  const ground=finite(plan.groundHeightAt?.(x,z),plan.end.y);
  return {x,z,y:feetY,feetY,height:Math.max(0,feetY-ground),clearance:feetY-plan.maxObstacleTop,done:progress>=1,progress,phase};
}
